import { AlertFactory, AlertState } from 'alerts/Alert'
import Coupon from 'billing/models/Coupon'
import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import Job from 'jobs/Job'
import { action, computed, observable, makeObservable } from 'mobx'
import { task, TaskGroup } from 'mobx-task'
import { Observable, firstValueFrom } from 'rxjs'
import { filter, first, share, switchMap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { fromStream } from 'utils/mobx-util'
import { filterNonNil } from 'utils/rx-util'
import ModalViewModel from '../../infra/ModalViewModel'
import { QueueChange } from './Persister'
import { AlertPort, CouponPort } from './ports'

export default class CouponSelectionViewModel extends ModalViewModel {
  /**
   * The selected coupon, if any.
   */
  @observable selected: Coupon | null = null

  /**
   * The text for the code that the user wants to add.
   */
  @observable codeToAdd = ''

  /**
   * Task group.
   */
  tasks
  private $ = {
    member: fromStream(null, this.member$),
    job: fromStream(Job.none, this.job$),
  }

  constructor(
    private isActive$: Observable<boolean>,
    private member$: Observable<Member | null>,
    private job$: Observable<Job>,
    private queueChange: QueueChange,
    private adapter: CouponPort & AlertPort,
    private _isDisabled: () => boolean,
    private newAlert: AlertFactory,
  ) {
    super(isActive$)
    makeObservable(this)
    this.fetchCouponsQuery$.subscribe()
    this.close$.subscribe(this.addCoupon.reset)
    this.tasks = TaskGroup<any, any>([
      this.fetchCouponInfo,
      this.fetchCoupons,
      this.select,
      this.remove,
    ])
  }

  /**
   * Whether coupon selection controls are disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Whether we can use coupons.
   */
  @computed
  get canUseCoupons() {
    return this.$.job().workspace.features.billing.couponsEnabled
  }

  /**
   * The available coupons.
   */
  @computed
  get coupons() {
    const member = this.$.member()
    return member ? this.adapter.couponsForMember(member) : []
  }

  /**
   * Whether a coupon is selected.
   */
  @computed
  get isCouponSelected() {
    return Boolean(this.selected)
  }

  /**
   * Whether we can add a coupon.
   */
  @computed
  get canAdd() {
    return (
      this.codeToAdd.length > 0 &&
      !this.addCoupon.pending &&
      !this.select.pending
    )
  }

  /**
   * Fetches coupons by IDs
   *
   * @param couponIds
   * @param ct
   */
  fetchCouponInfo = task.resolved(
    async (couponIds: string[], ct: CancellationToken) => {
      // Wait until the selected Member has loaded.
      await this.waitForMember()

      const couponId = Id(couponIds[0])
      if (this.selected?.id === couponId) {
        return
      }

      if (!couponId) {
        this.setCoupon(null)
        return
      }

      const coupon = await this.adapter.fetchCoupon(couponId)
      ct.throwIfCancelled()
      this.setCoupon(coupon)
    },
  )

  /**
   * Fetches coupons for the specified Workspace Member.
   */
  fetchCoupons = task(async (member: Member) => {
    await this.adapter.fetchCouponsForMember(member)
  })

  /**
   * Fetch coupons whenever the Member changes,
   * @private
   */
  private fetchCouponsQuery$ = this.member$.pipe(
    filterNonNil(),
    switchMap((member) =>
      this.showing$.pipe(
        filter(Boolean),
        first(),
        switchMap(() => this.fetchCoupons(member)),
      ),
    ),
    share(),
  )

  /**
   * Selects the coupon.
   *
   * @param coupon
   */
  select = task.resolved(async (coupon: Coupon | null) => {
    if (!coupon) {
      await this.queueChange({
        type: 'CouponCleared',
      })
    } else {
      const result = await this.queueChange({
        coupon,
        type: 'CouponSelected',
      })

      if (result.errorMessage) {
        this.newAlert().fromError(result.errorMessage).show()
      }
    }

    this.close()
  })

  /**
   * Attempts to add a coupon.
   */
  addCoupon = task.resolved(async () => {
    const code = this.codeToAdd.trim()
    if (!code) {
      throw new Error('Please enter a code.')
    }

    const member = this.$.member()
    if (!member) {
      throw new Error('No workspace selected.')
    }

    const coupon = await this.adapter.addCouponToMember(member, code)
    if (!coupon) {
      throw new Error('Oops! That does not appear to be a valid coupon!')
    }

    await this.select(coupon)
    this.adapter
      .newAlert()
      .saying('Success!', 'Your discount has been applied.')
      .withState(AlertState.Success)
      .autoDismiss()
      .show()
  })

  /**
   * Sets the code.
   *
   * @param code
   */
  @action.bound
  setCode(code: string) {
    // Whenever we type something, reset the task state
    // so any displayed error disappears.
    this.addCoupon.reset()
    this.codeToAdd = code.toUpperCase()
  }

  /**
   * Sets the coupon.
   *
   * @param coupon
   */
  @action.bound
  setCoupon(coupon: Coupon | null) {
    this.selected = coupon
  }

  /**
   * Clears the coupon.
   */
  remove = task.resolved(async () => {
    return this.select(null)
  })

  /**
   * Waits for the Workspace Member to have been selected.
   * @private
   */
  private async waitForMember() {
    await firstValueFrom(this.member$.pipe(first((m) => m !== Member.none)), {
      defaultValue: null,
    })
  }
}
